import type { CompanyId } from "@repo/app-types/entities/company";
import {
  parseFuzzySearchResourceKey,
  serializeFuzzySearchResourceKey,
  type FuzzySearchResourceKey,
} from "@repo/app-types/entities/search";

/**
 * @file
 * localStorage-backed history for the search modal: recent search queries and
 * recently opened search results. Both are scoped per company so switching
 * accounts never surfaces another company's history, and both are stored
 * newest-first with a small cap.
 */

const RECENT_SEARCHES_STORAGE_KEY = "alpharun.search.recentSearches";
const RECENTLY_OPENED_STORAGE_KEY = "alpharun.search.recentlyOpenedResults";

/** Store a few more than we show so deleted resources still leave 3 to render. */
const MAX_STORED_ENTRIES = 10;

export function readRecentSearchQueries(
  companyId: CompanyId | undefined,
): readonly string[] {
  return readStringList(
    buildStorageKey(RECENT_SEARCHES_STORAGE_KEY, companyId),
  );
}

export function recordRecentSearchQuery(
  companyId: CompanyId | undefined,
  query: string,
): void {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return;
  }

  prependToStringList(
    buildStorageKey(RECENT_SEARCHES_STORAGE_KEY, companyId),
    trimmedQuery,
    (existing) => existing.toLowerCase() === trimmedQuery.toLowerCase(),
  );
}

export function readRecentlyOpenedResultKeys(
  companyId: CompanyId | undefined,
): readonly FuzzySearchResourceKey[] {
  return readStringList(
    buildStorageKey(RECENTLY_OPENED_STORAGE_KEY, companyId),
  ).flatMap((serializedKey) => {
    const key = parseFuzzySearchResourceKey(serializedKey);
    return key ? [key] : [];
  });
}

export function recordRecentlyOpenedResult(
  companyId: CompanyId | undefined,
  resourceKey: FuzzySearchResourceKey,
): void {
  const serializedKey = serializeFuzzySearchResourceKey(resourceKey);
  prependToStringList(
    buildStorageKey(RECENTLY_OPENED_STORAGE_KEY, companyId),
    serializedKey,
    (existing) => existing === serializedKey,
  );
}

function buildStorageKey(
  prefix: string,
  companyId: CompanyId | undefined,
): string | undefined {
  return companyId ? `${prefix}.${companyId}` : undefined;
}

function readStringList(storageKey: string | undefined): readonly string[] {
  if (!storageKey) {
    return [];
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) {
      return [];
    }

    const parsed: unknown = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((entry): entry is string => typeof entry === "string");
  } catch {
    // Storage unavailable (e.g. privacy mode) or corrupted content.
    return [];
  }
}

function prependToStringList(
  storageKey: string | undefined,
  value: string,
  isDuplicateOf: (existing: string) => boolean,
): void {
  if (!storageKey) {
    return;
  }

  const entries = [
    value,
    ...readStringList(storageKey).filter((entry) => !isDuplicateOf(entry)),
  ].slice(0, MAX_STORED_ENTRIES);

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(entries));
  } catch {
    // Best-effort: history is a convenience, never block the interaction.
  }
}
